import React, { useState, useEffect } from "react";
import { elakayAutoText } from "../../pageInside/dataElakay";


const UNIT_OPTIONS = [
  { id: "day", get label() { return elakayAutoText("h_987b9ced08d4ac", "Day"); } },
  { id: "week", get label() { return elakayAutoText("h_f82be68a7fb4e7", "Week"); } },
  { id: "month", get label() { return elakayAutoText("h_082bc378cd60e1", "Month"); } },
  { id: "year", get label() { return elakayAutoText("h_879e32326c5217", "Year"); } },
  { id: "custom", get label() { return elakayAutoText("h_d993d77dae41c7", "Custom Date"); } },
];

const YEAR_PRESETS = [
  { id: "this", get label() { return elakayAutoText("h_77528c94d9a370", "This Year"); } },
  { id: "last", get label() { return elakayAutoText("h_ab9f828234e9c3", "Last Year"); } },
  { id: "2", get label() { return elakayAutoText("h_047f9ebf5c1f60", "Last 2 Years"); } },
  { id: "3", get label() { return elakayAutoText("h_c01171016efb3c", "Last 3 Years"); } },
  { id: "5", get label() { return elakayAutoText("h_fe2b8840e2c424", "Last 5 Years"); } },
  { id: "6", get label() { return elakayAutoText("h_f352c3672d1bf1", "Last 6 Years"); } },
  { id: "custom", get label() { return elakayAutoText("h_dce04fd315530c", "Custom…"); } },
];

export const DEFAULT_DATE_FILTER = {
  unit: "year",
  dayCount: 7,
  weekCount: 1,
  monthCount: 1,
  yearPreset: "this",
  yearCustomCount: 10,
  customStart: "",
  customEnd: "",
  asc: "desc",
};

// Maps the toolbar's own filter shape to the real backend params (orderDate/myd/startDate/endDate
// -- exactly what listOrders() already sends, see auth/index.js and listOrdersUsers in
// elakay_controllers/order.js). The backend's "years" mode only ever matches ONE calendar year
// (never a rolling multi-year window), so "Last N Years" (N>1) reuses the already-correct rolling
// "months" mechanism instead (myd: N*12) rather than inventing new server behavior.
export const buildDateFilterParams = (filter) => {
  const currentYear = new Date().getFullYear();
  switch (filter.unit) {
    case "day":
      return { orderDate: "days", myd: Math.max(1, Number(filter.dayCount) || 1) };
    case "week":
      return { orderDate: "days", myd: Math.max(1, Number(filter.weekCount) || 1) * 7 };
    case "month":
      return { orderDate: "months", myd: Math.max(1, Number(filter.monthCount) || 1) };
    case "year":
      if (filter.yearPreset === "this") return { orderDate: "years", myd: currentYear };
      if (filter.yearPreset === "last") return { orderDate: "years", myd: currentYear - 1 };
      if (filter.yearPreset === "custom") {
        return { orderDate: "months", myd: Math.max(1, Number(filter.yearCustomCount) || 1) * 12 };
      }
      return { orderDate: "months", myd: Math.max(1, Number(filter.yearPreset) || 2) * 12 };
    case "custom":
      return filter.customStart && filter.customEnd
        ? { orderDate: "custom", startDate: filter.customStart, endDate: filter.customEnd }
        : { orderDate: "years", myd: currentYear };
    default:
      return { orderDate: "years", myd: currentYear };
  }
};

// Compact, professional toolbar: only the input(s) matching the CURRENTLY selected unit render at
// all (never all of Day/Week/Month/Year/Custom's fields at once). Typing a count or picking a
// custom date does not refetch on every keystroke -- it's a local draft, committed on blur/Enter
// (counts) or the explicit Apply button (custom range), same "explicit commit" convention this
// codebase's own /c/orders filter form (pageInside/filersOrder.jsx) already uses instead of
// live-refetching per keystroke.
const DateRangeToolbar = ({ filter, onChange, onReset }) => {
  const [draft, setDraft] = useState(filter);
  useEffect(() => setDraft(filter), [filter]);

  const commitUnit = (unit) => onChange({ ...filter, unit });
  const commitYearPreset = (yearPreset) => onChange({ ...filter, unit: "year", yearPreset });
  const commitCount = (field) => onChange({ ...filter, [field]: draft[field] });
  const handleCountKeyDown = (field) => (e) => {
    if (e.key === "Enter") commitCount(field);
  };
  const applyCustomRange = () => {
    onChange({ ...filter, unit: "custom", customStart: draft.customStart, customEnd: draft.customEnd });
  };

  return (
    <div className="ph-toolbar">
      <div className="ph-filter-main">
        <div className="ph-filter-group" role="group" aria-label={elakayAutoText("h_92e4460b2825ec", "Filter by date range")}>
          {UNIT_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`ph-filter-btn${filter.unit === option.id ? " ph-filter-btn--active" : ""}`}
              aria-pressed={filter.unit === option.id}
              onClick={() => commitUnit(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>

        {filter.unit === "day" && (
          <label className="ph-count-input">
            <span>{elakayAutoText("h_d1c69a859a9638", "Last")}</span>
            <input
              type="number"
              min="1"
              value={draft.dayCount}
              onChange={(e) => setDraft((d) => ({ ...d, dayCount: e.target.value }))}
              onBlur={() => commitCount("dayCount")}
              onKeyDown={handleCountKeyDown("dayCount")}
              aria-label={elakayAutoText("h_cf117048b21832", "Number of days")}
            />
            <span>{elakayAutoText("h_4ceca77bac96f2", "Day(s)")}</span>
          </label>
        )}

        {filter.unit === "week" && (
          <label className="ph-count-input">
            <span>{elakayAutoText("h_d1c69a859a9638", "Last")}</span>
            <input
              type="number"
              min="1"
              value={draft.weekCount}
              onChange={(e) => setDraft((d) => ({ ...d, weekCount: e.target.value }))}
              onBlur={() => commitCount("weekCount")}
              onKeyDown={handleCountKeyDown("weekCount")}
              aria-label={elakayAutoText("h_3908d1e9fc4e36", "Number of weeks")}
            />
            <span>{elakayAutoText("h_8225e1c0b0d6bb", "Week(s)")}</span>
          </label>
        )}

        {filter.unit === "month" && (
          <label className="ph-count-input">
            <span>{elakayAutoText("h_d1c69a859a9638", "Last")}</span>
            <input
              type="number"
              min="1"
              value={draft.monthCount}
              onChange={(e) => setDraft((d) => ({ ...d, monthCount: e.target.value }))}
              onBlur={() => commitCount("monthCount")}
              onKeyDown={handleCountKeyDown("monthCount")}
              aria-label={elakayAutoText("h_46135870cd839e", "Number of months")}
            />
            <span>{elakayAutoText("h_59f8a6d4bd122a", "Month(s)")}</span>
          </label>
        )}

        {filter.unit === "year" && (
          <div className="ph-year-controls">
            <select value={filter.yearPreset} onChange={(e) => commitYearPreset(e.target.value)} aria-label={elakayAutoText("h_c106f48888c0d7", "Year range")}>
              {YEAR_PRESETS.map((preset) => (
                <option key={preset.id} value={preset.id}>{preset.label}</option>
              ))}
            </select>
            {filter.yearPreset === "custom" && (
              <label className="ph-count-input">
                <span>{elakayAutoText("h_d1c69a859a9638", "Last")}</span>
                <input
                  type="number"
                  min="1"
                  value={draft.yearCustomCount}
                  onChange={(e) => setDraft((d) => ({ ...d, yearCustomCount: e.target.value }))}
                  onBlur={() => commitCount("yearCustomCount")}
                  onKeyDown={handleCountKeyDown("yearCustomCount")}
                  aria-label={elakayAutoText("h_95e80435be2689", "Number of years")}
                />
                <span>{elakayAutoText("h_e1322da9076aaf", "Year(s)")}</span>
              </label>
            )}
          </div>
        )}

        {filter.unit === "custom" && (
          <div className="ph-custom-range">
            <input
              type="date"
              value={draft.customStart}
              onChange={(e) => setDraft((d) => ({ ...d, customStart: e.target.value }))}
              aria-label={elakayAutoText("h_ff99f5b5f7948d", "Start date")}
            />
            <span>{elakayAutoText("h_4374aaee247fb2", "to")}</span>
            <input
              type="date"
              value={draft.customEnd}
              onChange={(e) => setDraft((d) => ({ ...d, customEnd: e.target.value }))}
              aria-label={elakayAutoText("h_89d10cd6c1e143", "End date")}
            />
            <button
              type="button"
              className="ph-btn ph-btn-primary ph-btn-sm"
              disabled={!draft.customStart || !draft.customEnd}
              onClick={applyCustomRange}
            >{elakayAutoText("h_cfea419c3b4e8b", "Apply")}</button>
          </div>
        )}

        <button type="button" className="ph-reset-btn" onClick={onReset}>{elakayAutoText("h_44c57abd888a66", "Reset")}</button>
      </div>

      <label className="ph-sort-select">
        <span className="ph-sort-select-label">{elakayAutoText("h_adc4e96a478be0", "Sort")}</span>
        <select value={filter.asc} onChange={(e) => onChange({ ...filter, asc: e.target.value })} aria-label={elakayAutoText("h_6c168cda8cd0e6", "Sort purchases")}>
          <option value="desc">{elakayAutoText("h_f5ec7772ca9b92", "Newest first")}</option>
          <option value="asc">{elakayAutoText("h_6a99c65c33fe7c", "Oldest first")}</option>
        </select>
      </label>
    </div>
  );
};

export default DateRangeToolbar;
